import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getUserContext } from './UserContext';
import ForbiddenPage from './ForbiddenPage';
import { fetchMyAllowedScreens, getCachedAllowedScreens, isScreenAllowed } from '../../Utils/CommonUtils';

interface PermissionGateProps {
  element: React.ReactElement;
  screenKey: string;
  userRoles?: string[];
}

const PermissionGate: React.FC<PermissionGateProps> = ({ element, screenKey, userRoles }) => {
  const user = getUserContext(); 
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const authStatusChecked = useRef(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const checkAuthentication = () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        return decoded && decoded.exp * 1000 > Date.now() ? decoded : null;
      } catch (e) {
        setError(e as Error);
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!authStatusChecked.current) {
      const decodedToken = checkAuthentication();
      setIsAuthenticated(decodedToken ? true : false);

      if (!decodedToken) {
        setHasPermission(false);
      } else {
        const cachedScreens = getCachedAllowedScreens();
        if (cachedScreens.length > 0) {
          setHasPermission(isScreenAllowed(screenKey, cachedScreens));
        } else {
          setHasPermission(null);
          fetchMyAllowedScreens(user?.role)
            .then((screens) => {
              setHasPermission(isScreenAllowed(screenKey, screens));
            })
            .catch(() => {
              const userRole = String(user?.role || '').toUpperCase();
              const allowedByLegacyRoles = Boolean(userRole && (userRoles?.includes('*') || userRoles?.includes(userRole)));
              setHasPermission(allowedByLegacyRoles);
            });
        }
      } 

      authStatusChecked.current = true;

      if (!decodedToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('name');
        localStorage.removeItem('role');
        localStorage.removeItem('branch');
        setShouldNavigate(true);
      }
    }
  }, [screenKey, user?.role, userRoles]);

  useEffect(() => {
    if (shouldNavigate) {
      window.location.reload(); // Or navigate('/login');
    }
  }, [shouldNavigate]);

  useEffect(() => {
    // Reset auth status check on location change
    authStatusChecked.current = false;
  }, [location]);

  if (isAuthenticated === null) {
    // Optionally render a loading spinner or similar while checking authentication
    return <div>Loading...</div>;
  }

  if (isAuthenticated && hasPermission === null) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated && hasPermission) {
    return React.cloneElement(element, { userRoles });
  } else if (hasPermission === false) {
    return <ForbiddenPage/>;
  }

  return null;
};

export default PermissionGate;
